import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, IconButton, LinearProgress, Link, List, ListItem, Stack, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayCircleIcon from '@mui/icons-material/PlayCircleOutline';
import { cancelExport, deleteExport, fetchExport, fetchExports, isActiveExport, startExport, type EditingAllowance, type ExportRecord, EditingApiError } from '../../services/editingApi';
import { debugLog } from '../../utils/debugLog';
import { formatMinutes, formatMs, type SaveFirstResult } from './editStudioModel';
import SendToEditorDialog from './SendToEditorDialog';

interface ExportsPanelProps {
  editId: string;
  /** The edit's source, for "Create a project from this edit". */
  source?: { videoId?: string | null; multicamSetId?: string | null };
  editTitle: string;
  editVersion: number;
  /** Unsaved changes: Export saves them first. */
  dirty: boolean;
  /** Saves now and waits, so the export is the version on screen. */
  prepare?: () => Promise<SaveFirstResult>;
  /** Another tab saved first: nothing can be exported until the creator chooses. */
  conflict?: { onKeepMine: () => void; onLoadTheirs: () => void } | null;
  /** The server found a newer save from another tab. */
  onConflict?: (message: string) => void;
  outputDurationMs: number;
  resolution: string;
  aspect: string;
  allowance: EditingAllowance | null;
  onAllowanceChanged: () => void;
  /** A finished export, for a toast (and a notification when the tab is in the background). */
  onFinished?: (record: ExportRecord) => void;
  /** For tests: how often a running export is asked about. */
  pollMs?: number;
}

const STATUS_LABEL: Record<ExportRecord['status'], string> = {
  queued: 'Waiting to start',
  preparing: 'Getting ready',
  rendering: 'Rendering',
  uploading: 'Saving the file',
  done: 'Ready',
  failed: 'Didn’t work',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const STATUS_COLOUR: Record<ExportRecord['status'], 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  queued: 'default', preparing: 'primary', rendering: 'primary', uploading: 'primary', done: 'success', failed: 'error', cancelled: 'default', expired: 'warning',
};

const formatDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '');
const formatDay = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');
const shapeOf = (record: ExportRecord): string => {
  if (!record.width || !record.height) return '';
  const ratio = record.width / record.height;
  if (Math.abs(ratio - 9 / 16) < 0.02) return '9:16';
  if (Math.abs(ratio - 1) < 0.02) return '1:1';
  if (Math.abs(ratio - 4 / 5) < 0.02) return '4:5';
  if (Math.abs(ratio - 16 / 9) < 0.02) return '16:9';
  return `${record.width}×${record.height}`;
};

/** About how long a running export has left, from its progress so far (null until it can tell). */
export function timeLeftMs(record: ExportRecord, nowMs: number, startedAtMs: number | null): number | null {
  if (!startedAtMs || record.progress <= 0.03 || record.progress >= 1) return null;
  const elapsed = nowMs - startedAtMs;
  if (elapsed < 3000) return null;
  return Math.max(0, (elapsed / record.progress) * (1 - record.progress));
}

/** Start an export of this edit, watch it render, watch and download it. */
const ExportsPanel: React.FC<ExportsPanelProps> = ({ editId, source, editTitle, editVersion, dirty, prepare, conflict = null, onConflict, outputDurationMs, resolution, aspect, allowance, onAllowanceChanged, onFinished, pollMs = 4000 }) => {
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState<'saving' | 'starting' | null>(null);
  const [problem, setProblem] = useState<{ message: string; code: string | null } | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [watching, setWatching] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  /** When each running export was first seen rendering (for the time left). */
  const renderingSince = useRef<Record<string, number>>({});
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true; // React 18 development double-mounts: the cleanup below must not stick.
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const list = await fetchExports(editId);
      if (mounted.current) setExports(list);
    } catch (error) {
      debugLog('Exports could not be loaded', error);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [editId]);

  useEffect(() => { load(); }, [load]);

  // Ask about the running ones until they finish.
  useEffect(() => {
    const running = exports.filter((item) => isActiveExport(item.status));
    if (running.length === 0) return undefined;
    running.forEach((item) => { if (item.status === 'rendering' && !renderingSince.current[item.id]) renderingSince.current[item.id] = Date.now(); });
    const timer = window.setTimeout(async () => {
      const updates = await Promise.all(running.map((item) => fetchExport(item.id).catch(() => null)));
      if (!mounted.current) return;
      const finished = updates.filter((update): update is ExportRecord => Boolean(update && !isActiveExport(update.status)));
      setExports((current) => current.map((item) => updates.find((u) => u && u.id === item.id) || item));
      if (finished.length) {
        onAllowanceChanged();
        finished.forEach((record) => { if (record.status === 'done') onFinished?.(record); });
        const ready = finished.find((record) => record.status === 'done');
        if (ready) setWatching(ready.id);
      }
    }, pollMs);
    return () => window.clearTimeout(timer);
  }, [exports, onAllowanceChanged, onFinished, pollMs]);

  const start = async () => {
    setProblem(null);
    try {
      // Save first and wait: the export takes the saved edit, so it must be the one on screen.
      let version: number | undefined;
      if (prepare) {
        setStarting('saving');
        const ready = await prepare();
        if (!ready.ok) {
          // A conflict shows its own choice (Keep mine or Load theirs) above.
          if (!ready.conflict) setProblem({ message: ready.message, code: null });
          return;
        }
        version = ready.version;
      }
      setStarting('starting');
      const created = await startExport(editId, version);
      setExports((current) => [created, ...current]);
      onAllowanceChanged();
      // Ask once whether finished exports may notify while the tab is in the background.
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => undefined);
    } catch (error) {
      const apiError = error instanceof EditingApiError ? error : new EditingApiError(error);
      if (apiError.code === 'EDIT_CONFLICT' && onConflict) onConflict(apiError.message);
      else setProblem({ message: apiError.message, code: apiError.code });
    } finally {
      if (mounted.current) setStarting(null);
    }
  };

  const cancel = async (id: string) => {
    try {
      const cancelled = await cancelExport(id);
      setExports((current) => current.map((item) => (item.id === id ? cancelled : item)));
      onAllowanceChanged();
    } catch (error) {
      setProblem({ message: error instanceof Error ? error.message : 'It couldn’t be cancelled.', code: null });
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this export? The file goes now rather than when it expires. The edit stays.')) return;
    try {
      await deleteExport(id);
      setExports((current) => current.filter((item) => item.id !== id));
      if (watching === id) setWatching(null);
    } catch (error) {
      setProblem({ message: error instanceof Error ? error.message : 'It couldn’t be deleted.', code: null });
    }
  };

  const copyLink = async (record: ExportRecord) => {
    if (!record.downloadUrl) return;
    try {
      await navigator.clipboard.writeText(record.downloadUrl);
      setCopied(record.id);
      window.setTimeout(() => { if (mounted.current) setCopied(null); }, 2500);
    } catch {
      setProblem({ message: 'The link couldn’t be copied. Right-click Download and copy it instead.', code: null });
    }
  };

  const minutes = Math.ceil(outputDurationMs / 1000) / 60;
  const left = allowance ? Math.max(0, allowance.exportMinutesPerMonth - allowance.exportMinutesUsed) : null;
  const running = exports.some((item) => isActiveExport(item.status));
  const noPlan = Boolean(allowance && (!allowance.enabled || allowance.exportMinutesPerMonth <= 0));
  const overAllowance = Boolean(allowance && !noPlan && left !== null && minutes > left + 1e-9);
  const shapeName = aspect === 'source' ? '' : ` · ${aspect}`;

  return (
    <Box>
      {noPlan ? (
        <Card variant="outlined" sx={{ mb: 2, borderColor: 'primary.light' }} data-testid="export-upgrade">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Export it with Pro or Pro Max</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Everything you make here is saved. Upgrade to render it as an MP4:</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
              <Box sx={{ flex: 1, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2">Pro · $14.99/month</Typography>
                <Typography variant="caption" color="text.secondary">60 minutes of 1080p exports and 100 assistant prompts a month</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2">Pro Max · $39.99/month</Typography>
                <Typography variant="caption" color="text.secondary">300 minutes up to 4K, 500 prompts, transcripts and captions</Typography>
              </Box>
            </Stack>
            {/* Billing opens with its plan picker when asked this way (BillingLocationState.openPlanPicker). */}
            <Button variant="contained" component={RouterLink} to="/billing" state={{ openPlanPicker: true }} data-testid="choose-plan">Choose a plan</Button>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Renders in the cloud as an MP4 you can watch and download for {allowance?.exportRetentionDays ?? 30} days. This one is {formatMs(outputDurationMs)} at {resolution === '2160p' ? '4K' : resolution}{shapeName}
          {allowance ? `, about ${formatMinutes(minutes)} of your ${formatMinutes(allowance.exportMinutesPerMonth)} a month (${formatMinutes(left ?? 0)} left).` : '.'}
        </Typography>
      )}
      {conflict ? (
        <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="export-conflict">
          This edit was changed in another tab or window, so what you see here isn’t what’s saved and an export now wouldn’t match it. Keep mine saves this version over the other; Load theirs opens the other one.
          {/* Under the text: beside it, the panel's width squeezes the message into a thin column. */}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button color="inherit" size="small" variant="outlined" onClick={conflict.onKeepMine} data-testid="export-keep-mine">Keep mine</Button>
            <Button color="inherit" size="small" onClick={conflict.onLoadTheirs}>Load theirs</Button>
          </Stack>
        </Alert>
      ) : dirty ? <Alert severity="info" sx={{ mb: 1.5 }}>Export saves your latest changes first.</Alert> : null}
      {overAllowance ? <Alert severity="warning" sx={{ mb: 1.5 }}>That’s more than this month’s minutes left. Trim it, or <Link component={RouterLink} to="/billing">upgrade</Link>.</Alert> : null}
      {problem ? (
        <Alert severity={problem.code === 'PLAN_UPGRADE_REQUIRED' || problem.code === 'EXPORT_LIMIT' ? 'warning' : 'error'} sx={{ mb: 1.5 }} onClose={() => setProblem(null)}>
          {problem.message}
          {problem.code === 'PLAN_UPGRADE_REQUIRED' ? <> <Link component={RouterLink} to="/billing">Billing</Link></> : null}
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="contained" onClick={start} disabled={Boolean(starting) || running || noPlan || Boolean(conflict)} data-testid="start-export">
          {running ? 'Exporting…' : starting === 'saving' ? 'Saving…' : starting ? 'Starting…' : 'Export'}
        </Button>
        <Button variant="outlined" onClick={() => setSendOpen(true)} data-testid="send-to-editor">Send to my editor</Button>
      </Stack>
      {sent ? <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setSent(null)}>{sent}</Alert> : null}
      <SendToEditorDialog
        open={sendOpen}
        editId={editId}
        editVersion={editVersion}
        dirty={dirty}
        prepare={prepare}
        source={source}
        onClose={() => setSendOpen(false)}
        onSent={(result) => {
          setSendOpen(false);
          setSent(`Sent to “${result.projectName}”: ${result.tasksAdded} task${result.tasksAdded === 1 ? '' : 's'}${result.tasksReplaced ? ` (replacing ${result.tasksReplaced} from the earlier send)` : ''}.`);
        }}
      />
      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>Exports</Typography>
      {loading ? <LinearProgress /> : exports.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nothing exported yet. Exports appear here, ready to watch and download.</Typography>
      ) : (
        <List dense disablePadding data-testid="export-list">
          {exports.map((item) => {
            const active = isActiveExport(item.status);
            const remaining = active ? timeLeftMs(item, Date.now(), renderingSince.current[item.id] || null) : null;
            const facts = [item.resolution === '2160p' ? '4K' : item.resolution, shapeOf(item), formatMs(item.outputDurationMs), item.sizeBytes ? `${Math.round((item.sizeBytes / 1024 / 1024) * 10) / 10} MB` : ''].filter(Boolean).join(' · ');
            return (
              <ListItem key={item.id} disableGutters sx={{ display: 'block', py: 1, borderBottom: '1px solid', borderColor: 'divider' }} data-testid={`export-${item.id}`}>
                {/* Narrow panel: the name and its state, then the facts, then what you can do with it. */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" noWrap title={item.title || editTitle} sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{item.title || editTitle}</Typography>
                  <Chip size="small" label={STATUS_LABEL[item.status]} color={STATUS_COLOUR[item.status]} />
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">{facts} · version {item.editVersion} · {formatDate(item.createdAt)}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  {active ? <Button size="small" onClick={() => cancel(item.id)}>Cancel</Button> : null}
                  {item.status === 'done' && item.downloadUrl ? (
                    <>
                      <Button size="small" variant="outlined" component="a" href={item.downloadUrl} download>Download</Button>
                      <Tooltip title={watching === item.id ? 'Hide the player' : 'Watch it'}><IconButton size="small" aria-label="Watch the export" aria-pressed={watching === item.id} onClick={() => setWatching(watching === item.id ? null : item.id)}><PlayCircleIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={copied === item.id ? 'Copied' : 'Copy the download link'}><IconButton size="small" aria-label="Copy the download link" onClick={() => copyLink(item)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                    </>
                  ) : null}
                  <Box sx={{ flex: 1 }} />
                  {!active ? <Tooltip title="Delete the export"><IconButton size="small" aria-label="Delete the export" onClick={() => remove(item.id)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip> : null}
                </Stack>
                {active ? (
                  <Box sx={{ mt: 0.5 }}>
                    <LinearProgress variant={item.progress > 0 ? 'determinate' : 'indeterminate'} value={item.progress * 100} />
                    <Typography variant="caption" color="text.secondary">
                      {item.progress > 0 ? `${Math.round(item.progress * 100)}%` : STATUS_LABEL[item.status]}{remaining !== null ? ` · about ${Math.max(1, Math.round(remaining / 1000))} s left` : ''}
                      {' '}· you can leave this page; it keeps going, and a long one emails you when it’s ready.
                    </Typography>
                  </Box>
                ) : null}
                {item.status === 'failed' && item.error ? <Typography variant="caption" color="error">{item.error}</Typography> : null}
                {item.status === 'done' ? (
                  <Typography variant="caption" color="text.secondary">
                    {item.expiresAt ? `Kept until ${formatDay(item.expiresAt)}` : ''}{copied === item.id ? ' · link copied (it works for a day)' : ''}
                    {item.sidecars.map((sidecar) => <React.Fragment key={sidecar.name}> · <Link href={sidecar.url} download>{sidecar.name}</Link></React.Fragment>)}
                  </Typography>
                ) : null}
                {watching === item.id && item.downloadUrl ? (
                  <Box sx={{ mt: 1, bgcolor: '#000', borderRadius: 1, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video src={item.downloadUrl} controls playsInline preload="metadata" style={{ maxWidth: '100%', maxHeight: 360, display: 'block' }} data-testid={`export-player-${item.id}`} />
                  </Box>
                ) : null}
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default ExportsPanel;
